import { Navigate, Route, Routes } from "react-router-dom";
import { Suspense, useEffect } from "react";
import { protectedRoutes } from "./routes";
import { LANGUAGE } from "services/general";
import { useProfile } from "api/shared";
import LoadingPage from "components/shared/LoadingPage";
import ChunkLoadErrorBoundary from "components/shared/ChunkLoadErrorBoundary";
import { lazyWithChunkRetry } from "utils/lazyWithChunkRetry";
import PermissionRouteGate from "./PermissionRouteGate";
import { useFaviconBootstrap } from "hooks";
import { identifyPostHogUser } from "posthog";

// Vite static glob — must stay at module level (cannot be dynamic).
// eager: false (default) → each module is loaded only when first requested.
const modules = import.meta.glob(
  [
    "../../modules/**/*.{js,jsx}",
    "../../components/**/*.{js,jsx}",
  ],
  { eager: false }
);

// Cache resolved lazy components so the same filePath is never looked up twice.
const moduleCache = new Map();

const importModule = (filePath) => {
  if (moduleCache.has(filePath)) return moduleCache.get(filePath);

  // Resolution order mirrors webpack's default behaviour:
  //  1. Exact file  (e.g. add.jsx / add.js)
  //  2. Folder index (e.g. add/index.jsx / add/index.js)
  const candidates = [
    `../../${filePath}.jsx`,
    `../../${filePath}.js`,
    `../../${filePath}/index.jsx`,
    `../../${filePath}/index.js`,
  ];

  const loader = candidates.reduce((found, key) => found || modules[key], null);

  if (!loader) {
    console.error(
      `[importModule] Component not found for filePath: "${filePath}"\n` +
      `Tried:\n${candidates.map((c) => `  • ${c}`).join("\n")}`
    );
    const errorComponent = lazyWithChunkRetry(() =>
      Promise.reject(new Error(`Module not found: ${filePath}`))
    );
    moduleCache.set(filePath, errorComponent);
    return errorComponent;
  }

  const component = lazyWithChunkRetry(loader);
  moduleCache.set(filePath, component);
  return component;
};

const protectedRouteComponents = protectedRoutes.map(
  ({
    path,
    filePath,
    permissionId,
    createPermissionId,
    editPermissionId,
    entityIdParam,
    skipPermissionGate,
  }) => ({
    path: `/:type/${path.toLowerCase()}`,
    component: importModule(filePath),
    permissionId,
    createPermissionId,
    editPermissionId,
    entityIdParam,
    skipPermissionGate,
  }),
);

const AppRoutes = () => {
  const { data: profile } = useProfile();

  useEffect(() => {
    if (profile) {
      localStorage.setItem("user", JSON.stringify(profile));
      identifyPostHogUser(profile);
    }
  }, [profile]);

  useFaviconBootstrap();

  return (
    <Routes>
      {protectedRouteComponents.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <ChunkLoadErrorBoundary key={route.path}>
              <Suspense fallback={<LoadingPage />}>
                <PermissionRouteGate
                  permissionId={route.permissionId}
                  createPermissionId={route.createPermissionId}
                  editPermissionId={route.editPermissionId}
                  entityIdParam={route.entityIdParam}
                  skipPermissionGate={route.skipPermissionGate}
                >
                  <route.component />
                </PermissionRouteGate>
              </Suspense>
            </ChunkLoadErrorBoundary>
          }
        />
      ))}

      <Route
        path="*"
        exact={true}
        element={<Navigate replace to={`/${LANGUAGE}/home`} />}
      />
    </Routes>
  );
};
export default AppRoutes;
